import React, { useEffect, useMemo } from 'react';
import { AlgoliaConnectorContext } from './AlgoliaConnectorContext';
import { liteClient as algoliasearch } from 'algoliasearch/lite';
import { useFrame } from '../../frame';

type AlgoliaClient = ReturnType<typeof algoliasearch>;

const emptyAlgoliaClient = {
    search: async (requests: unknown[]) => ({
        results: requests.map(() => ({
            hits: [],
            nbHits: 0,
            page: 0,
            nbPages: 0,
            hitsPerPage: 0,
            processingTimeMS: 0,
            exhaustiveNbHits: true,
            query: '',
            params: '',
        })),
    }),
    searchForFacetValues: async (requests: unknown[]) => ({
        results: requests.map(() => ({
            facetHits: [],
            exhaustiveFacetsCount: true,
            processingTimeMS: 0,
        })),
    }),
    clearCache: async () => undefined,
} as unknown as AlgoliaClient;

type Props = {
    /**
     * Provide a refetch interval in milliseconds to clear the cache of the Algolia client.
     */
    refetchInterval?: number;
};

export const AlgoliaConnectorProvider = ({
    children,
    refetchInterval,
}: React.PropsWithChildren<Props>) => {
    const { data: frame } = useFrame();
    const algoliaApp = frame?.search?.algolia?.appKey ?? '';
    const algoliaToken = frame?.search?.algolia?.apiToken ?? '';

    const client = useMemo(() => {
        if (algoliaApp && algoliaToken) {
            return algoliasearch(algoliaApp, algoliaToken);
        }

        console.warn('[AlgoliaConnectorProvider] Missing Algolia credentials; using empty search client.', {
            missingAppKey: !algoliaApp,
            missingApiToken: !algoliaToken,
            frameId: frame?.id,
            culture: frame?.culture,
            market: frame?.market,
            hasSearchSettings: Boolean(frame?.search),
            hasAlgoliaSettings: Boolean(frame?.search?.algolia),
        });

        return emptyAlgoliaClient;
    }, [algoliaApp, algoliaToken, frame?.culture, frame?.id, frame?.market, frame?.search]);

    useEffect(() => {
        if (refetchInterval) {
            const refetchIntervalTimer = setInterval(function () {
                client.clearCache();
            }, refetchInterval * 1000);
            return () => clearInterval(refetchIntervalTimer);
        }
    }, [client, refetchInterval]);

    return (
        <AlgoliaConnectorContext.Provider value={client}>
            {children}
        </AlgoliaConnectorContext.Provider>
    );
};
